// file: apps/web-pharmacy/src/components/alertes/criticalSection.js
/**
 * PHARMAKON — LA SOUPAPE DE SÉCURITÉ DU COMPTOIR.
 *
 * PLAN_mobile_pharma_owner_manager §3.5, « le carve-out POS — une exigence
 * dure, pas une politesse ».
 *
 * LE PROBLÈME. La surcouche bloquante d'une alerte URGENTE atterrit sur des
 * écrans qui incluent le POINT DE VENTE, où `require-open-caisse` et
 * `bindCaissierCaisse` lient les mutations à une session de caisse vivante. Une
 * surcouche qui vole le focus pendant un `POST /ventes/:id/validate`, un
 * `/ventes/libre`, un `/ventes/chifa` ou un mouvement de trésorerie est un
 * INCIDENT DE CAISSE.
 *
 * LES DEUX MOITIÉS DU CONTRAT, toutes deux obligatoires :
 *   (a) la surcouche NE SE MONTE JAMAIS pendant une mutation d'encaissement ;
 *   (b) elle FINIT TOUJOURS par se monter — au premier temps mort, et au plus
 *       tard après `max_defer_ms` (défaut 20 s), report journalisé.
 * Une soupape qui ne tient que (a) est une alerte urgente qu'on peut faire
 * taire indéfiniment en gardant une vente ouverte. C'est pour ça que (b) est
 * dans le même fichier et dans le même test.
 *
 * OÙ EST POSÉ LE CAPTEUR. Sur le CLIENT HTTP PARTAGÉ (`services/api.client.js`),
 * via des intercepteurs enregistrés depuis ICI — le fichier partagé n'est pas
 * modifié. C'est le seul point de passage commun aux ~10 sites de mutation
 * d'encaissement du POS (vente libre, CHIFA, validation, annulation, crédit,
 * avoir, retour, trésorerie, ouverture/clôture de caisse). Les instrumenter un
 * par un aurait voulu dire toucher dix écrans que ce lot ne possède pas — et
 * le onzième, ajouté demain, n'aurait rien su de la soupape.
 *
 * Ce module est SANS REACT : il se teste en environnement node, sans DOM.
 */

import {
  CHEMINS_CRITIQUES_POS,
  METHODES_MUTANTES,
  ALERTE_CLIENT_DEFAUTS,
} from "./alerte-equipe.config.js";

/** Nombre de sections critiques ouvertes. 0 = le comptoir est au repos. */
let profondeur = 0;
/** Horodatage d'ouverture de la PREMIÈRE section encore ouverte. */
let ouverteDepuis = null;
/** Abonnés notifiés à chaque transition occupé ⇄ repos. */
const abonnes = new Set();
/** Filets de sécurité par section (une requête qui ne revient jamais). */
const filets = new Map();
let sequence = 0;

function notifier() {
  const etat = { occupe: profondeur > 0, depuis: ouverteDepuis };
  for (const fn of [...abonnes]) {
    try {
      fn(etat);
    } catch {
      /* un abonné défaillant ne bloque jamais le comptoir */
    }
  }
}

/**
 * Ouvre une section critique. Rend la fonction de fermeture, IDEMPOTENTE :
 * l'appeler deux fois ne décrémente qu'une fois (sinon une double libération
 * ferait passer le comptoir pour libre alors qu'une autre vente est en vol).
 *
 * @param {string} [libelle] pour le journal de report
 * @param {number} [filetMs] libération de secours si rien ne referme
 * @returns {() => void}
 */
export function ouvrirSectionCritique(libelle = "pos", filetMs = 60_000) {
  const id = (sequence += 1);
  profondeur += 1;
  if (profondeur === 1) ouverteDepuis = Date.now();
  notifier();

  let ferme = false;
  const fermer = () => {
    if (ferme) return;
    ferme = true;
    const filet = filets.get(id);
    if (filet) {
      clearTimeout(filet);
      filets.delete(id);
    }
    profondeur = Math.max(0, profondeur - 1);
    if (profondeur === 0) ouverteDepuis = null;
    notifier();
  };

  // Filet : une requête qui ne revient jamais (socket morte) ne doit pas
  // sceller le comptoir et faire taire une alerte urgente pour toujours.
  if (filetMs > 0) {
    const t = setTimeout(() => {
      if (!ferme) {
         
        console.warn(
          `[AlerteEquipe] section critique « ${libelle} » libérée par le filet de sécurité`,
        );
        fermer();
      }
    }, filetMs);
    if (typeof t?.unref === "function") t.unref();
    filets.set(id, t);
  }

  return fermer;
}

/** Le comptoir est-il en train d'encaisser ? */
export function sectionCritiqueOccupee() {
  return profondeur > 0;
}

/** Depuis quand (epoch ms), ou null au repos. */
export function sectionCritiqueDepuis() {
  return ouverteDepuis;
}

/** S'abonner aux transitions occupé ⇄ repos. Rend la fonction de désabonnement. */
export function surSectionCritique(fn) {
  abonnes.add(fn);
  return () => abonnes.delete(fn);
}

/** Une URL de requête est-elle une mutation d'encaissement ? */
export function estCheminCritique(url = "", method = "get") {
  if (!METHODES_MUTANTES.includes(String(method).toLowerCase())) return false;
  const chemin = String(url ?? "");
  return CHEMINS_CRITIQUES_POS.some((re) => re.test(chemin));
}

/**
 * Décide s'il faut MONTER la surcouche bloquante maintenant.
 *
 * C'est le cœur testable du contrat : au repos on monte tout de suite ; occupé
 * on attend — mais jamais au-delà du plafond, et le dépassement est signalé
 * pour être journalisé.
 *
 * @param {object} p
 * @param {boolean} p.occupe        une mutation d'encaissement est en vol
 * @param {number|null} p.enFileDepuis  epoch ms du premier report, ou null
 * @param {number} p.maintenant     epoch ms
 * @param {number} p.maxDeferMs     plafond de report (annoncé par le serveur)
 * @returns {{ monter: boolean, force: boolean, attenduMs: number }}
 */
export function decisionMontage({
  occupe,
  enFileDepuis,
  maintenant = Date.now(),
  maxDeferMs = ALERTE_CLIENT_DEFAUTS.maxDeferMs,
}) {
  if (!occupe) return { monter: true, force: false, attenduMs: 0 };
  // `!= null` et pas un test de véracité : un horodatage VAUT parfois 0 (epoch,
  // et c'est le cas dans les tests à minuteur simulé), et `0` est faux. Avec un
  // test de véracité, une alerte mise en file à cet instant-là n'accumulerait
  // jamais de temps d'attente — donc ne franchirait jamais le plafond, donc ne
  // se monterait jamais. C'est-à-dire la moitié (b) du contrat, cassée.
  const attenduMs = enFileDepuis != null ? maintenant - enFileDepuis : 0;
  if (attenduMs >= maxDeferMs) {
    // (b) — la moitié qui empêche « garder une vente ouverte » de faire taire
    // une alerte urgente pour toujours.
    return { monter: true, force: true, attenduMs };
  }
  return { monter: false, force: false, attenduMs };
}

// ─────────────────────────────────────────────────────────────────────────────
// Le capteur : posé sur l'ADAPTATEUR du client HTTP partagé, sans le modifier
// ─────────────────────────────────────────────────────────────────────────────

let installe = false;
/** L'adaptateur d'origine, gardé pour la remise à zéro des tests. */
let adaptateurDOrigine = null;
let clientEquipe = null;

/**
 * Installe le capteur (idempotent — un double montage de l'hôte n'en pose pas
 * deux). Rend `true` s'il a réellement installé.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POURQUOI L'ADAPTATEUR ET NON DES INTERCEPTEURS — ceci est une CORRECTION,
 * trouvée par le smoke vivant (`e2e/scratch-impl-c1/b4/`).
 *
 * La version précédente ouvrait la section dans un intercepteur de requête et
 * la refermait dans un intercepteur de réponse, en retrouvant sa fonction de
 * fermeture sur `response.config` / `error.config`. Ça ne pouvait PAS marcher
 * sur ce client-ci : `services/api.client.js` enregistre, AU CHARGEMENT DU
 * MODULE, un intercepteur de réponse qui DÉBALLE l'enveloppe axios —
 * `(res) => res.data` du côté succès, `Promise.reject(err.response?.data ?? err)`
 * du côté erreur. Les intercepteurs de réponse s'exécutent dans l'ORDRE
 * D'ENREGISTREMENT ; le nôtre, posé au montage de `AlerteHost`, arrivait donc
 * toujours APRÈS, et ne recevait plus qu'une charge utile JSON, sans `config`
 * et sans notre marque. La section n'était JAMAIS relâchée par le chemin
 * normal : seul le filet de sécurité de 60 s finissait par le faire.
 *
 * Conséquence exacte, et c'est une régression de comportement, pas un détail :
 * après CHAQUE mutation d'encaissement le comptoir restait « occupé » une
 * minute entière. Le plafond de report tenait toujours (une alerte urgente
 * finissait par se monter), mais la moitié « elle se monte au premier temps
 * mort » du §3.5 était morte — une alerte urgente arrivée juste après une
 * vente attendait 20 s au lieu de s'afficher tout de suite.
 *
 * L'adaptateur est le seul point de passage qui voie la requête PARTIR et
 * REVENIR avant toute transformation, quel que soit l'ordre des intercepteurs
 * — et c'est la couture que l'application elle-même utilise déjà pour la porte
 * hors-ligne (`offline/offlineGate.js`) et pour le mode bêta. On l'enveloppe,
 * on ne la remplace pas.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * @param {import('axios').AxiosInstance} client
 */
export function installerCapteurPos(client) {
  const precedent = client?.defaults?.adapter;
  if (installe || typeof precedent !== "function") return false;
  installe = true;
  adaptateurDOrigine = precedent;
  clientEquipe = client;

  client.defaults.adapter = (config) => {
    let fermer = null;
    try {
      if (estCheminCritique(config?.url, config?.method)) {
        fermer = ouvrirSectionCritique(`${config.method} ${config.url}`);
      }
    } catch {
      /* le capteur ne doit JAMAIS faire échouer une vente */
    }

    let promesse;
    try {
      promesse = precedent(config);
    } catch (e) {
      // Échec SYNCHRONE de l'adaptateur : on relâche avant de repropager.
      fermer?.();
      throw e;
    }
    if (!promesse || typeof promesse.then !== "function") {
      fermer?.();
      return promesse;
    }
    // `finally` relâche sur les DEUX branches sans toucher à la valeur ni à la
    // raison : une vente qui échoue libère le comptoir exactement comme une
    // vente qui réussit.
    return promesse.finally(() => fermer?.());
  };
  return true;
}

/** Remise à zéro — tests uniquement. */
export function __reinitialiserSectionCritique() {
  profondeur = 0;
  ouverteDepuis = null;
  abonnes.clear();
  for (const t of filets.values()) clearTimeout(t);
  filets.clear();
  // On REND son adaptateur au client : sans ça, deux cas de test à la suite
  // empileraient les enveloppes sur le même faux client.
  if (clientEquipe?.defaults && adaptateurDOrigine) {
    clientEquipe.defaults.adapter = adaptateurDOrigine;
  }
  adaptateurDOrigine = null;
  clientEquipe = null;
  installe = false;
}
